/**
 * Base API Client
 * Shared utilities and HTTP methods for all API modules
 */

import { extractErrorData, mapStatusToError } from "./errors";
import { interceptors, type RequestConfig } from "./interceptors";
import { logger } from "../utils/logger";
import { validateApiResponse } from "../utils/typeValidation";
import type { ZodSchema } from "zod";

// Use Vite's environment variable convention
export const API_BASE_URL =
  import.meta.env.VITE_API_URL || "http://localhost:8080";

// Prefix for all backend API calls routed through ingress
const API_PREFIX = "/api";

/**
 * Builds the full URL for an API endpoint
 */
function buildURL(endpoint: string): string {
  const normalizedEndpoint = endpoint.startsWith(API_PREFIX)
    ? endpoint
    : `${API_PREFIX}${endpoint}`;
  return `${API_BASE_URL}${normalizedEndpoint}`;
}

/**
 * Handles the response from fetch requests.
 * Checks for errors and parses JSON.
 */
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorData = await extractErrorData(response);
    throw mapStatusToError(response.status, errorData.message);
  }

  // Handle cases where the response might be empty (e.g., 204 No Content)
  const text = await response.text();
  if (!text) {
    return {} as T;
  }

  try {
    return JSON.parse(text) as T;
  } catch (e) {
    logger.error(
      "Failed to parse JSON response",
      e instanceof Error ? e : new Error(String(e)),
      {
        responseText: text.substring(0, 200), // Limit log size
      }
    );
    throw new Error("Invalid JSON response from server.");
  }
}

/**
 * Handles blob responses
 */
async function handleBlobResponse(response: Response): Promise<Blob> {
  if (!response.ok) {
    const errorData = await extractErrorData(response);
    throw mapStatusToError(response.status, errorData.message);
  }
  return response.blob();
}

/**
 * Performs a GET request to the API.
 * @param endpoint - API endpoint path
 * @param requiresAuth - Whether authentication is required (default: true)
 * @param schema - Optional Zod schema for runtime response validation
 */
export async function get<T>(
  endpoint: string,
  requiresAuth: boolean = true,
  schema?: ZodSchema<T>
): Promise<T> {
  const url = buildURL(endpoint);
  const config: RequestConfig = {
    url,
    method: "GET",
    requiresAuth,
  };

  try {
    const finalConfig = await interceptors.executeRequestInterceptors(config);
    const response = await fetch(url, finalConfig);
    const interceptedResponse = await interceptors.executeResponseInterceptors(
      response,
      finalConfig
    );

    // Use validation if schema provided, otherwise use existing handler
    if (schema) {
      return await validateApiResponse<T>(interceptedResponse, schema);
    }

    return handleResponse<T>(interceptedResponse);
  } catch (error) {
    const processedError = await interceptors.executeErrorInterceptors(
      error as Error,
      config
    );
    throw processedError;
  }
}

/**
 * Performs a POST request to the API.
 */
export async function post<T>(
  endpoint: string,
  data: unknown,
  requiresAuth: boolean = false
): Promise<T> {
  const url = buildURL(endpoint);
  const config: RequestConfig = {
    url,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
    requiresAuth,
  };

  try {
    const finalConfig = await interceptors.executeRequestInterceptors(config);
    const response = await fetch(url, finalConfig);
    const interceptedResponse = await interceptors.executeResponseInterceptors(
      response,
      finalConfig
    );
    return handleResponse<T>(interceptedResponse);
  } catch (error) {
    const processedError = await interceptors.executeErrorInterceptors(
      error as Error,
      config
    );
    throw processedError;
  }
}

/**
 * Performs a PATCH request to the API.
 */
export async function patch<T>(
  endpoint: string,
  data: unknown,
  requiresAuth: boolean = true
): Promise<T> {
  const url = buildURL(endpoint);
  const config: RequestConfig = {
    url,
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
    requiresAuth,
  };

  try {
    const finalConfig = await interceptors.executeRequestInterceptors(config);
    const response = await fetch(url, finalConfig);
    const interceptedResponse = await interceptors.executeResponseInterceptors(
      response,
      finalConfig
    );
    return handleResponse<T>(interceptedResponse);
  } catch (error) {
    const processedError = await interceptors.executeErrorInterceptors(
      error as Error,
      config
    );
    throw processedError;
  }
}

/**
 * Performs a PUT request to the API.
 */
export async function put<T>(
  endpoint: string,
  data: unknown,
  requiresAuth: boolean = true
): Promise<T> {
  const url = buildURL(endpoint);
  const config: RequestConfig = {
    url,
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
    requiresAuth,
  };

  try {
    const finalConfig = await interceptors.executeRequestInterceptors(config);
    const response = await fetch(url, finalConfig);
    const interceptedResponse = await interceptors.executeResponseInterceptors(
      response,
      finalConfig
    );
    return handleResponse<T>(interceptedResponse);
  } catch (error) {
    const processedError = await interceptors.executeErrorInterceptors(
      error as Error,
      config
    );
    throw processedError;
  }
}

/**
 * Performs a DELETE request to the API.
 */
export async function del<T>(
  endpoint: string,
  requiresAuth: boolean = true
): Promise<T> {
  const url = buildURL(endpoint);
  const config: RequestConfig = {
    url,
    method: "DELETE",
    requiresAuth,
  };

  try {
    const finalConfig = await interceptors.executeRequestInterceptors(config);
    const response = await fetch(url, finalConfig);
    const interceptedResponse = await interceptors.executeResponseInterceptors(
      response,
      finalConfig
    );
    return handleResponse<T>(interceptedResponse);
  } catch (error) {
    const processedError = await interceptors.executeErrorInterceptors(
      error as Error,
      config
    );
    throw processedError;
  }
}

/**
 * Performs a POST request that returns a Blob.
 */
export async function postBlob(
  endpoint: string,
  data: unknown,
  requiresAuth: boolean = true
): Promise<Blob> {
  const url = buildURL(endpoint);
  const config: RequestConfig = {
    url,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
    requiresAuth,
  };

  try {
    const finalConfig = await interceptors.executeRequestInterceptors(config);
    const response = await fetch(url, finalConfig);
    const interceptedResponse = await interceptors.executeResponseInterceptors(
      response,
      finalConfig
    );
    return handleBlobResponse(interceptedResponse);
  } catch (error) {
    const processedError = await interceptors.executeErrorInterceptors(
      error as Error,
      config
    );
    throw processedError;
  }
}
